import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

export interface OpenMeteoWeatherNow {
  tempCurrent: number | null;
  tempMax: number | null;
  tempMin: number | null;
  windSpeed: number | null;
  windDir: number | null;
  humidity: number | null;
  pressure: number | null;
  precip: number | null;
}

export interface OpenMeteoCoords {
  latitude: number;
  longitude: number;
  timezone?: string;
}

export function getWindDirTextShort(deg?: number | null) {
  if (deg == null) return '-';
  if (deg >= 337.5 || deg < 22.5) return 'N';
  if (deg >= 22.5 && deg < 67.5) return 'NE';
  if (deg >= 67.5 && deg < 112.5) return 'E';
  if (deg >= 112.5 && deg < 157.5) return 'SE';
  if (deg >= 157.5 && deg < 202.5) return 'S';
  if (deg >= 202.5 && deg < 247.5) return 'SW';
  if (deg >= 247.5 && deg < 292.5) return 'W';
  if (deg >= 292.5 && deg < 337.5) return 'NW';
  return '-';
}

// Varsayılan: Kiyanly, Türkmenistan (ana sayfa ile aynı)
const DEFAULT_COORDS: OpenMeteoCoords = {
  latitude: 39.85,
  longitude: 53.05,
  timezone: 'Asia/Ashgabat',
};

async function fetchOpenMeteoWeatherNow(coords: OpenMeteoCoords): Promise<OpenMeteoWeatherNow> {
  const res = await axios.get('https://api.open-meteo.com/v1/forecast', {
    params: {
      latitude: coords.latitude,
      longitude: coords.longitude,
      current: 'temperature_2m,relative_humidity_2m,wind_speed_10m,wind_direction_10m,surface_pressure,precipitation',
      daily: 'temperature_2m_max,temperature_2m_min',
      timezone: coords.timezone ?? DEFAULT_COORDS.timezone,
      forecast_days: 1,
    },
  });

  const d = res.data;
  return {
    tempCurrent: d.current?.temperature_2m ?? null,
    tempMax: d.daily?.temperature_2m_max?.[0] ?? null,
    tempMin: d.daily?.temperature_2m_min?.[0] ?? null,
    windSpeed: d.current?.wind_speed_10m ?? null,
    windDir: d.current?.wind_direction_10m ?? null,
    humidity: d.current?.relative_humidity_2m ?? null,
    pressure: d.current?.surface_pressure ?? null,
    precip: d.current?.precipitation ?? null,
  };
}

export function useOpenMeteoWeatherNow(options?: { coords?: OpenMeteoCoords; enabled?: boolean }) {
  const coords = options?.coords ?? DEFAULT_COORDS;
  const enabled = options?.enabled ?? true;

  return useQuery({
    queryKey: ['weather-openmeteo-now', coords.latitude, coords.longitude, coords.timezone ?? DEFAULT_COORDS.timezone],
    queryFn: () => fetchOpenMeteoWeatherNow(coords),
    staleTime: 1000 * 60 * 15,
    retry: 2,
    enabled,
  });
}

